'use client'

import { useState, type FormEvent } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { LOCALES, localeLabel, type Locale } from '@synthec/types'
import { cn } from '@synthec/ui'
import { Container } from './container'
import { Logo } from './logo'

/** A curated Productos menu group (marketing taxonomy: "Soluciones de
 *  lubricación" / "Soluciones complementarias"): an optional landing link plus
 *  the sub-items shown in the flyout. Built per-country in the locale layout, so
 *  every href already resolves to a live page (slugs differ by market). */
export type NavItem = { title: string; href: string }
export type NavFamily = {
  title: string
  href?: string
  children: NavItem[]
}

const SIMPLE_LINKS = [
  { href: '/marcas/', label: 'Marcas' },
  { href: '/sostenibilidad/', label: 'Sostenibilidad' },
  { href: '/casos-de-exito/', label: 'Casos de éxito' },
]
const EXPLORA_LINKS = [
  { href: '/blog/', label: 'Blog' },
  { href: '/news/', label: 'News' },
  { href: '/brochures/', label: 'Recursos' },
]

function Flag({ locale, className }: { locale: Locale; className?: string }) {
  return (
    <svg viewBox="0 0 24 16" className={className} aria-hidden>
      {locale === 'peru' ? (
        <>
          <rect width="24" height="16" fill="#fff" />
          <rect width="6" height="16" fill="#D91023" />
          <rect x="18" width="6" height="16" fill="#D91023" />
        </>
      ) : (
        <>
          <rect width="24" height="16" fill="#FCD116" />
          <rect y="8" width="24" height="4" fill="#003893" />
          <rect y="12" width="24" height="4" fill="#CE1126" />
        </>
      )}
    </svg>
  )
}

const Caret = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden className="opacity-70">
    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export function Nav({ locale, families = [] }: { locale: Locale; families?: NavFamily[] }) {
  const pathname = usePathname()
  const router = useRouter()
  const base = `/${locale}`

  const [openDd, setOpenDd] = useState<null | 'productos' | 'explora' | 'pais'>(null)
  const [activeFamily, setActiveFamily] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [mobileOpen, setMobileOpen] = useState(false)

  const closeAll = () => {
    setOpenDd(null)
    setMobileOpen(false)
    setSearchOpen(false)
  }

  const switchPath = (target: Locale) => {
    const rest = pathname.replace(/^\/(peru|colombia)/, '')
    return `/${target}${rest || '/'}`
  }

  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    const q = search.trim()
    router.push(`${base}/shop/${q ? `?q=${encodeURIComponent(q)}` : ''}`)
    closeAll()
    setSearch('')
  }

  const activeFam = families.find((f) => f.title === activeFamily) ?? families[0]

  const linkCls =
    'rounded-md px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:text-accent'

  return (
    <header className="sticky top-0 z-40 bg-emerald-700 text-white shadow-[0_4px_20px_rgba(0,0,0,0.35)]">
      <div className="relative bg-emerald-700">
        <Container className="relative z-10 flex h-16 items-center gap-5">
        <Link href={`${base}/`} className="mr-auto text-lg lg:mr-2" onClick={closeAll}>
          <Logo wordmarkClassName="text-white" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {/* Productos mega-menu */}
          <div
            className="relative"
            onMouseEnter={() => setOpenDd('productos')}
            onMouseLeave={() => setOpenDd(null)}
          >
            <button
              type="button"
              className={cn(linkCls, 'flex items-center gap-1.5', openDd === 'productos' && 'text-accent')}
              onClick={() => setOpenDd((o) => (o === 'productos' ? null : 'productos'))}
              aria-expanded={openDd === 'productos'}
            >
              Productos <Caret />
            </button>
            {openDd === 'productos' && (
              <div className="absolute left-0 top-full flex w-[640px] max-w-[88vw] overflow-hidden rounded-2xl border border-black/5 bg-white text-ink-900 shadow-2xl">
                <ul className="w-[46%] border-r border-ink-100 p-2">
                  {families.map((f) => {
                    const on = activeFam?.title === f.title
                    const rowCls = cn(
                      'flex w-full items-center justify-between rounded-lg px-3.5 py-3 text-left text-[15px] font-semibold transition-colors',
                      on ? 'bg-accent/10 text-accent' : 'text-ink-900 hover:bg-ink-50',
                    )
                    const inner = (
                      <>
                        {f.title}
                        <span className="text-lg leading-none text-accent">›</span>
                      </>
                    )
                    return (
                      <li key={f.title} onMouseEnter={() => setActiveFamily(f.title)}>
                        {f.href ? (
                          <Link href={f.href} onClick={closeAll} className={rowCls}>
                            {inner}
                          </Link>
                        ) : (
                          <button type="button" className={rowCls}>
                            {inner}
                          </button>
                        )}
                      </li>
                    )
                  })}
                  <li>
                    <Link
                      href={`${base}/shop/`}
                      onClick={closeAll}
                      className="flex items-center justify-between rounded-lg px-3.5 py-3 text-[15px] font-semibold text-brand hover:bg-ink-50"
                    >
                      Ver todos los productos
                      <span className="text-lg leading-none">→</span>
                    </Link>
                  </li>
                </ul>
                <div className="w-[54%] bg-ink-50 p-2">
                  {activeFam && activeFam.children.length > 0 ? (
                    activeFam.children.map((c) => (
                      <Link
                        key={c.title}
                        href={c.href}
                        onClick={closeAll}
                        className="block rounded-lg px-3.5 py-2.5 text-sm text-ink-700 transition-colors hover:bg-white hover:text-accent"
                      >
                        {c.title}
                      </Link>
                    ))
                  ) : (
                    <p className="px-3.5 py-3 text-sm text-ink-500">
                      {activeFam ? `Explora ${activeFam.title}.` : 'Explora el catálogo completo.'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {SIMPLE_LINKS.map((item) => (
            <Link key={item.href} href={`${base}${item.href}`} className={linkCls} onClick={closeAll}>
              {item.label}
            </Link>
          ))}

          {/* Explora dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenDd('explora')}
            onMouseLeave={() => setOpenDd(null)}
          >
            <button
              type="button"
              className={cn(linkCls, 'flex items-center gap-1.5', openDd === 'explora' && 'text-accent')}
              onClick={() => setOpenDd((o) => (o === 'explora' ? null : 'explora'))}
              aria-expanded={openDd === 'explora'}
            >
              Explora <Caret />
            </button>
            {openDd === 'explora' && (
              <div className="absolute left-0 top-full min-w-[220px] rounded-2xl border border-black/5 bg-white p-2 text-ink-900 shadow-2xl">
                {EXPLORA_LINKS.map((item) => (
                  <Link
                    key={item.href}
                    href={`${base}${item.href}`}
                    onClick={closeAll}
                    className="block rounded-lg px-3.5 py-2.5 text-sm text-ink-700 transition-colors hover:bg-ink-50 hover:text-accent"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link href={`${base}/empresa/`} className={linkCls} onClick={closeAll}>
            Empresa
          </Link>
        </nav>

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => {
              setSearchOpen((v) => !v)
              setOpenDd(null)
            }}
            className="grid h-10 w-10 place-items-center rounded-full text-white transition-colors hover:bg-white/10 hover:text-accent"
            aria-label="Buscar"
            aria-expanded={searchOpen}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </button>

          <Link
            href={`${base}/contactanos/`}
            onClick={closeAll}
            className="hidden rounded-full bg-white px-5 py-2.5 text-sm font-bold text-ink-900 shadow-sm transition hover:bg-white/90 sm:inline-flex"
          >
            Contáctanos
          </Link>

          {/* Country switcher (PE / CO) */}
          <div
            className="relative hidden sm:block"
            onMouseEnter={() => setOpenDd('pais')}
            onMouseLeave={() => setOpenDd(null)}
          >
            <button
              type="button"
              onClick={() => setOpenDd((o) => (o === 'pais' ? null : 'pais'))}
              className="flex items-center gap-2 rounded-full border border-white/50 bg-black/15 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-white"
              aria-expanded={openDd === 'pais'}
            >
              <Flag locale={locale} className="h-3.5 w-5 rounded-[2px] ring-1 ring-black/10" />
              {localeLabel[locale]}
              <Caret />
            </button>
            {openDd === 'pais' && (
              <div className="absolute right-0 top-full min-w-[160px] rounded-2xl border border-black/5 bg-white p-2 text-ink-900 shadow-2xl">
                {LOCALES.map((l) => (
                  <Link
                    key={l}
                    href={switchPath(l)}
                    onClick={closeAll}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-ink-50',
                      l === locale ? 'font-semibold text-accent' : 'text-ink-700',
                    )}
                  >
                    <Flag locale={l} className="h-3.5 w-5 rounded-[2px] ring-1 ring-black/10" />
                    {localeLabel[l]}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full text-white hover:bg-white/10 lg:hidden"
            onClick={() => {
              setMobileOpen((v) => !v)
              setOpenDd(null)
            }}
            aria-label="Menú"
            aria-expanded={mobileOpen}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </Container>
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="border-t border-white/10 bg-emerald-800">
          <Container className="py-3">
            <form onSubmit={onSearch}>
              <input
                autoFocus
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar productos, marcas…"
                aria-label="Buscar"
                className="w-full rounded-full border border-accent bg-white/5 px-5 py-3.5 text-base text-white outline-none placeholder:text-white/50"
              />
            </form>
          </Container>
        </div>
      )}

      {/* Mobile panel */}
      {mobileOpen && (
        <Container className="flex max-h-[80vh] flex-col gap-1 overflow-auto pb-5 lg:hidden">
          <p className="px-2 pt-2 text-xs font-semibold uppercase tracking-widest text-white/40">Productos</p>
          {families.map((f) => (
            <div key={f.title}>
              {f.href ? (
                <Link
                  href={f.href}
                  onClick={closeAll}
                  className="block rounded-lg px-3 py-2 text-sm font-semibold text-white/90 hover:bg-white/10"
                >
                  {f.title}
                </Link>
              ) : (
                <p className="px-3 py-2 text-sm font-semibold text-white/90">{f.title}</p>
              )}
              {f.children.map((c) => (
                <Link
                  key={c.title}
                  href={c.href}
                  onClick={closeAll}
                  className="block rounded-lg px-5 py-1.5 text-sm text-white/70 hover:bg-white/10"
                >
                  {c.title}
                </Link>
              ))}
            </div>
          ))}
          <Link
            href={`${base}/shop/`}
            onClick={closeAll}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-accent hover:bg-white/10"
          >
            Ver todos los productos →
          </Link>

          <div className="my-2 h-px bg-white/10" />
          {[...SIMPLE_LINKS, ...EXPLORA_LINKS, { href: '/empresa/', label: 'Empresa' }].map((item) => (
            <Link
              key={item.href}
              href={`${base}${item.href}`}
              onClick={closeAll}
              className="rounded-lg px-3 py-2 text-sm font-medium text-white/85 hover:bg-white/10"
            >
              {item.label}
            </Link>
          ))}

          <Link
            href={`${base}/contactanos/`}
            onClick={closeAll}
            className="mt-2 rounded-full bg-accent px-4 py-2.5 text-center text-sm font-bold text-ink-900"
          >
            Contáctanos
          </Link>

          <div className="mt-3 flex gap-2">
            {LOCALES.map((l) => (
              <Link
                key={l}
                href={switchPath(l)}
                onClick={closeAll}
                className={cn(
                  'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold',
                  l === locale ? 'bg-white text-ink-900' : 'bg-white/10 text-white',
                )}
              >
                <Flag locale={l} className="h-3.5 w-5 rounded-[2px]" />
                {localeLabel[l]}
              </Link>
            ))}
          </div>
        </Container>
      )}
    </header>
  )
}
